import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import {
  AlertCircle, ArrowRight, Building2, CalendarClock, CalendarDays, CheckCircle2, KeyRound, Landmark, MinusCircle,
  RefreshCw, Server, ShieldAlert, Unlink, Users,
} from 'lucide-react';
import { getBackendStatus, getChannelCatalog, getChannexUsage, getStats, listHotels, listPlans, listUsers } from '@/lib/api/admin';
import { formatDate, formatNumber, formatPercent, plural } from '@/lib/format';
import { daysUntil, periodOf } from '@/lib/period';
import { Badge } from '@/components/ui/badge';
import { Card, CardHeader, Stat } from '@/components/ui/card';
import { Notice, QueryError, Skeleton, Spinner } from '@/components/ui/feedback';
import { PageHeader } from '@/components/ui/page';
import { HBars } from '@/components/charts/lazy';
import { Strip } from '@/components/charts/strip';
import { ROLE_LABEL } from '@/features/users/roles';
import { deriveOrganizations, orgLabel } from '@/features/organizations/derive';

const CHECKS = [
  { key: 'postgres', label: 'PostgreSQL' },
  { key: 'redis',    label: 'Redis' },
  { key: 's3',       label: 'Объектное хранилище' },
] as const;

/**
 * The platform on one screen: four counts, what needs a hand today, how the
 * estate is shaped (channels, plans, roles, biggest clients), and whether the
 * thing underneath answers. Everything past the four counts is derived from
 * the three lists — the backend has no analytics endpoint yet, and these
 * views are what one would ask it for.
 */
export function OverviewPage() {
  const navigate = useNavigate();
  const stats = useQuery({ queryKey: ['admin', 'stats'], queryFn: getStats });
  const hotels = useQuery({ queryKey: ['admin', 'hotels'], queryFn: listHotels });
  const users = useQuery({ queryKey: ['admin', 'users'], queryFn: listUsers });
  const plans = useQuery({ queryKey: ['admin', 'plans'], queryFn: listPlans });
  const channex = useQuery({ queryKey: ['admin', 'channex-usage', periodOf(new Date())], queryFn: () => getChannexUsage(periodOf(new Date())) });
  const status = useQuery({ queryKey: ['backend-status'], queryFn: getBackendStatus, refetchInterval: 60_000 });
  const catalog = useQuery({ queryKey: ['channel-catalog'], queryFn: getChannelCatalog, staleTime: 5 * 60_000 });

  const orgs = useMemo(() => deriveOrganizations(hotels.data ?? [], users.data ?? [], channex.data), [hotels.data, users.data, channex.data]);

  const attention = useMemo(() => {
    const items: Array<{ key: string; icon: typeof AlertCircle; tone: 'danger' | 'warn'; text: string; to: string }> = [];
    for (const h of hotels.data ?? []) {
      if (h.syncError) items.push({ key: `sync:${h.id}`, icon: RefreshCw, tone: 'danger', text: `${h.name}: ошибка синхронизации — ${h.syncError}`, to: `/hotels/${h.id}` });
    }
    const report = channex.data;
    if (report) {
      const all = [...report.organizations.flatMap((o) => o.hotels), ...report.orphans];
      for (const p of all) {
        const d = daysUntil(p.removalDate);
        if (d !== null && d <= 45) items.push({ key: `rm:${p.propertyId}`, icon: CalendarClock, tone: d <= 14 ? 'danger' : 'warn', text: `Channex удалит «${p.title ?? p.propertyId}» через ${plural(d, ['день', 'дня', 'дней'])}`, to: '/channex-cost' });
      }
      if (report.orphans.length > 0) items.push({ key: 'orphans', icon: Unlink, tone: 'warn', text: `${plural(report.orphans.length, ['платный объект', 'платных объекта', 'платных объектов'])} в Channex без владельца`, to: '/channex-cost' });
      if (report.totals.unknown > 0) items.push({ key: 'unknown', icon: AlertCircle, tone: 'warn', text: `${plural(report.totals.unknown, ['объект', 'объекта', 'объектов'])} без данных о каналах — счёт за месяц занижен`, to: '/channex-cost' });
    }
    if (status.data && status.data.checks) {
      for (const { key, label } of CHECKS) if (status.data.checks[key] === 'error') items.push({ key: `chk:${key}`, icon: Server, tone: 'danger', text: `${label} не отвечает`, to: '/' });
    }
    for (const p of catalog.data?.providers ?? []) {
      if (!p.configured) items.push({ key: `prov:${p.id}`, icon: KeyRound, tone: 'warn', text: `Провайдер ${p.id} без доступов`, to: '/' });
    }
    return items;
  }, [hotels.data, channex.data, status.data, catalog.data]);

  const channelStrip = useMemo(() => {
    const list = hotels.data ?? [];
    const off = list.filter((h) => !h.isActive).length;
    const withCh = list.filter((h) => h.isActive && h.connectedChannels > 0).length;
    const without = list.filter((h) => h.isActive && h.connectedChannels === 0).length;
    return [
      { label: 'С каналами', value: withCh, tone: 'accent' as const },
      { label: 'Без каналов', value: without, tone: 'soft' as const },
      { label: 'Выключены', value: off, tone: 'gray' as const },
    ];
  }, [hotels.data]);

  const roleBars = useMemo(() => {
    const counts = new Map<string, number>();
    for (const u of users.data ?? []) {
      const k = u.role ?? 'none';
      counts.set(k, (counts.get(k) ?? 0) + 1);
    }
    return [...counts].map(([k, v]) => ({ name: k === 'none' ? 'Без членства' : ROLE_LABEL[k] ?? k, value: v }));
  }, [users.data]);

  const planBars = useMemo(() => (plans.data ?? []).map((p) => ({ name: p.name, value: p.subscriberCount, detail: p.isActive ? undefined : 'скрыт' })), [plans.data]);

  const topOrgs = useMemo(() => orgs.slice(0, 8).map((o) => ({ name: orgLabel(o), value: o.hotels.length, detail: `${plural(o.rooms, ['номер', 'номера', 'номеров'])}` })), [orgs]);

  const recentHotels = useMemo(() => [...(hotels.data ?? [])].sort((a, b) => b.createdAt.localeCompare(a.createdAt)).slice(0, 5), [hotels.data]);
  const recentUsers = useMemo(() => [...(users.data ?? [])].sort((a, b) => b.createdAt.localeCompare(a.createdAt)).slice(0, 5), [users.data]);

  const verified = users.data ? users.data.filter((u) => u.emailVerified).length : 0;
  const activeHotels = hotels.data ? hotels.data.filter((h) => h.isActive).length : 0;

  return (
    <>
      <PageHeader title="Обзор" subtitle="Вся платформа: кто на ней, что требует внимания и на чём это работает." />

      {stats.isError && <QueryError error={stats.error} onRetry={() => stats.refetch()} />}
      <div className="mb-6 flex flex-wrap gap-3">
        {stats.isPending ? [0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-[132px] min-w-[160px] flex-1" />) : stats.data && (
          <>
            <Stat label="Организации" value={formatNumber(stats.data.totalOrganizations)} icon={<Landmark size={18} />} onClick={() => navigate('/organizations')}
              hint={orgs.length > 0 && orgs.length !== stats.data.totalOrganizations ? `${orgs.length} с отелями или сотрудниками` : undefined} />
            <Stat label="Отели" value={formatNumber(stats.data.totalHotels)} icon={<Building2 size={18} />} tone="ok" onClick={() => navigate('/hotels')}
              hint={hotels.data ? `${formatNumber(activeHotels)} активных · ${formatPercent(activeHotels, hotels.data.length)}` : undefined} />
            <Stat label="Пользователи" value={formatNumber(stats.data.totalUsers)} icon={<Users size={18} />} tone="warn" onClick={() => navigate('/users')}
              hint={users.data ? `${formatNumber(verified)} с подтверждённым e-mail` : undefined} />
            <Stat label="Брони" value={formatNumber(stats.data.totalReservations)} icon={<CalendarDays size={18} />} tone="neutral" hint="за всё время" />
          </>
        )}
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader icon={<ShieldAlert size={18} />} title="Требует внимания" subtitle="Ошибки синхронизации, объекты под удаление в Channex, неполные данные, инфраструктура." />
          {(hotels.isPending || channex.isPending) ? <Spinner /> : attention.length === 0 ? (
            <Notice tone="ok" icon={<CheckCircle2 size={16} />} title="Всё спокойно" >Ни одной ошибки синхронизации, ничего не удаляется, все зависимости живы.</Notice>
          ) : (
            <ul className="divide-y divide-line">
              {attention.map(({ key, icon: Icon, tone, text, to }) => (
                <li key={key}>
                  <Link to={to} className="group flex items-start gap-3 py-2.5 text-sm hover:text-accent-ink">
                    <Icon size={15} className={tone === 'danger' ? 'mt-0.5 shrink-0 text-danger' : 'mt-0.5 shrink-0 text-warn'} />
                    <span className="min-w-0 flex-1 text-ink group-hover:text-accent-ink">{text}</span>
                    <ArrowRight size={14} className="mt-0.5 shrink-0 text-faint opacity-0 transition-opacity group-hover:opacity-100" />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card>
          <CardHeader icon={<RefreshCw size={18} />} title="Каналы у отелей" subtitle="Кто продаёт через channel manager." />
          {hotels.isPending ? <Skeleton className="h-16" /> : <Strip parts={channelStrip} />}
          {hotels.data && (
            <p className="mt-4 text-xs text-muted">
              Подключено {plural(hotels.data.reduce((s, h) => s + h.connectedChannels, 0), ['канал', 'канала', 'каналов'])} · {formatPercent(channelStrip[0].value, activeHotels)} активных отелей с каналами
            </p>
          )}
        </Card>

        <Card>
          <CardHeader title="Подписки по тарифам" subtitle="Сколько организаций на каждом тарифе." action={<Link to="/plans" className="text-xs font-semibold text-accent-ink hover:underline">Тарифы</Link>} />
          {plans.isPending ? <Skeleton className="h-28" /> : <HBars data={planBars} />}
        </Card>

        <Card>
          <CardHeader title="Пользователи по ролям" subtitle="Роль первого членства аккаунта." action={<Link to="/users" className="text-xs font-semibold text-accent-ink hover:underline">Пользователи</Link>} />
          {users.isPending ? <Skeleton className="h-28" /> : <HBars data={roleBars} />}
        </Card>

        <Card>
          <CardHeader title="Крупнейшие клиенты" subtitle="Организации по числу отелей." action={<Link to="/organizations" className="text-xs font-semibold text-accent-ink hover:underline">Все</Link>} />
          {hotels.isPending ? <Skeleton className="h-28" /> : <HBars data={topOrgs} />}
        </Card>

        <Card>
          <CardHeader icon={<Building2 size={18} />} title="Новые отели" subtitle="Последние зарегистрированные." />
          {hotels.isPending ? <Skeleton className="h-28" /> : (
            <ul className="divide-y divide-line">
              {recentHotels.map((h) => (
                <li key={h.id}>
                  <Link to={`/hotels/${h.id}`} className="flex items-center gap-3 py-2 text-sm hover:text-accent-ink">
                    <span className="min-w-0 flex-1 truncate font-medium text-ink">{h.name}</span>
                    <span className="text-xs text-muted">{h.city}</span>
                    <span className="text-xs text-faint tabular-nums">{formatDate(h.createdAt)}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card>
          <CardHeader icon={<Users size={18} />} title="Новые пользователи" subtitle="Последние зарегистрированные." />
          {users.isPending ? <Skeleton className="h-28" /> : (
            <ul className="divide-y divide-line">
              {recentUsers.map((u) => (
                <li key={u.id}>
                  <Link to={`/users?id=${u.id}`} className="flex items-center gap-3 py-2 text-sm hover:text-accent-ink">
                    <span className="min-w-0 flex-1 truncate font-medium text-ink">{u.displayName || u.email}</span>
                    {u.role && <Badge>{ROLE_LABEL[u.role] ?? u.role}</Badge>}
                    <span className="text-xs text-faint tabular-nums">{formatDate(u.createdAt)}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card>
          <CardHeader icon={<Server size={18} />} title="Бэкенд" subtitle="Настраивается на сервере, здесь только читается." />
          {status.isPending ? <Spinner /> : status.isError ? <QueryError error={status.error} /> : (
            <>
              <Notice
                tone={status.data.reachable ? 'ok' : 'warn'}
                icon={status.data.reachable ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
                title={status.data.reachable ? 'Отвечает — все зависимости живы' : status.data.checks ? 'Отвечает, но зависимость не в порядке' : 'Не отвечает из этого браузера'}
                className="mb-3"
              />
              <p className="mb-2 break-all font-mono text-[11px] text-faint">{status.data.apiBaseUrl}</p>
              {status.data.checks && (
                <ul className="divide-y divide-line">
                  {CHECKS.map(({ key, label }) => {
                    const state = status.data.checks?.[key] ?? 'error';
                    return (
                      <li key={key} className="flex items-center gap-3 py-2 text-sm">
                        {state === 'ok' ? <CheckCircle2 size={14} className="text-ok" /> : state === 'skipped' ? <MinusCircle size={14} className="text-faint" /> : <AlertCircle size={14} className="text-danger" />}
                        <span className="flex-1 text-ink">{label}</span>
                        <span className="font-mono text-[11px] text-faint">{state}</span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </>
          )}
        </Card>

        <Card>
          <CardHeader icon={<KeyRound size={18} />} title="Провайдеры каналов" subtitle="Через что бэкенд умеет продавать и есть ли у него доступы." />
          {catalog.isPending ? <Spinner /> : catalog.isError ? <QueryError error={catalog.error} /> : catalog.data.providers.length === 0 ? (
            <p className="text-xs text-muted">Этот бэкенд не отдаёт каталог провайдеров.</p>
          ) : (
            <ul className="divide-y divide-line">
              {catalog.data.providers.map((p) => (
                <li key={p.id} className="flex flex-wrap items-center gap-2 py-2.5">
                  <span className="text-sm font-semibold text-ink">{p.id}</span>
                  <span className="flex-1" />
                  <Badge tone={p.configured ? 'ok' : 'warn'} dot>{p.configured ? 'Настроен' : 'Нет доступов'}</Badge>
                  {p.capabilities.webhooks && (
                    <Badge tone={p.webhookReady ? 'ok' : 'warn'} dot>
                      {p.webhookReady ? (p.webhookSubscribed === false ? 'Вебхук не подписан' : 'Вебхук готов') : 'Нет вебхука'}
                    </Badge>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </>
  );
}
